// Event wizard review & activate (SLICE-EVENT-TICKET-PASS).
//
// The event analogue of StepReview / StepMembershipReview. Mirrors the server's
// EventTicketActivateSchema + collectEventTicketActivationGaps so the owner
// isn't surprised by a 422 (event needs a name, a valid date, a venue, and at
// least one named tier).

import {
  buildDefaultEventTicketDraft,
  collectEventTicketActivationGaps,
  type EventTicketConfig,
} from '@naya/shared'

import type { BuilderController } from '../hooks/useBuilder.js'
import type { WizardStepKey } from '../builderState.js'

function SummaryBlock({
  title,
  step,
  onEdit,
  children,
}: {
  title: string
  step: WizardStepKey
  onEdit: (step: WizardStepKey) => void
  children: React.ReactNode
}) {
  return (
    <div className="program-summary-block">
      <div className="program-summary-head">
        <h3>{title}</h3>
        <button
          type="button"
          className="program-link-btn"
          onClick={() => onEdit(step)}
        >
          Edit
        </button>
      </div>
      <div className="program-summary-body">{children}</div>
    </div>
  )
}

function eventWhen(config: EventTicketConfig): string {
  if (!config.starts_at) return 'No date set'
  const t = Date.parse(config.starts_at)
  if (Number.isNaN(t)) return 'No date set'
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(t))
}

export function StepEventReview({
  builder,
  onEditStep,
  onRequestActivate,
}: {
  builder: BuilderController
  onEditStep: (step: WizardStepKey) => void
  onRequestActivate: () => void
}) {
  const { draft } = builder
  const config: EventTicketConfig = {
    ...buildDefaultEventTicketDraft().type_config,
    ...((draft.type_config as EventTicketConfig | null) ?? {}),
  }

  const gaps = collectEventTicketActivationGaps({
    name: draft.name,
    onboarding_fields: draft.onboarding_fields,
    pass_design: draft.pass_design,
    back_links: draft.back_links,
    owner_terms: draft.owner_terms,
    type_config: config,
  })
  const canActivate = gaps.length === 0

  const namedTiers = config.tiers.filter((t) => t.name.trim())
  const enabledOptional = (['last_name', 'birthday', 'phone'] as const).filter(
    (f) => draft.onboarding_fields[f].enabled,
  )

  return (
    <div className="program-step">
      <h2>Review and activate</h2>
      <p className="program-step-sub">
        Check everything over. Once you activate, you can issue tickets and share
        the claim link.
      </p>

      <SummaryBlock title="Event" step="event" onEdit={onEditStep}>
        <p>
          {config.event_name || <em>(no name yet)</em>} · {eventWhen(config)}
        </p>
        <p className="program-summary-sub">
          {config.venue_name || '(no venue)'}
          {config.capacity != null ? ` · capacity ${config.capacity}` : ''}
        </p>
        {namedTiers.length > 0 && (
          <p className="program-summary-sub">
            {namedTiers.length} tier{namedTiers.length === 1 ? '' : 's'}:{' '}
            {namedTiers.map((t) => t.name.trim()).join(', ')}
          </p>
        )}
      </SummaryBlock>

      <SummaryBlock title="Sign-up info" step="onboarding" onEdit={onEditStep}>
        <p>
          First name, Email
          {enabledOptional.length > 0 && (
            <>
              {', '}
              {enabledOptional.map((f) => f.replace('_', ' ')).join(', ')}
            </>
          )}
        </p>
      </SummaryBlock>

      <SummaryBlock title="Pass front" step="front" onEdit={onEditStep}>
        <p className="program-summary-swatches">
          <span
            className="program-summary-swatch"
            style={{ background: draft.pass_design.primary_color }}
          />
          {draft.pass_design.primary_color}
          <span
            className="program-summary-swatch"
            style={{ background: draft.pass_design.text_color }}
          />
          {draft.pass_design.text_color}
        </p>
      </SummaryBlock>

      <SummaryBlock title="Pass back" step="back" onEdit={onEditStep}>
        <p>
          {draft.back_links.length} link
          {draft.back_links.length === 1 ? '' : 's'}
          {draft.owner_terms.trim() ? ' · custom terms added' : ''}
        </p>
      </SummaryBlock>

      {!canActivate && (
        <div className="program-gap-list" role="alert">
          <strong>Before you can activate:</strong>
          <ul>
            {gaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="program-activate-warning">
        After activating you can't change the sign-up fields. You can still tweak
        the event details, tiers, colors, links, and terms — changes apply to new
        tickets.
      </div>

      <button
        type="button"
        className="owner-btn owner-btn-primary program-activate-btn"
        disabled={!canActivate}
        onClick={onRequestActivate}
      >
        Activate event
      </button>
    </div>
  )
}
